import styled from "@emotion/styled";
import {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
} from "react";

import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { buildSubscriptionPlanKindMetricsPayload } from "@/domains/metrics/monetization";
import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonVariant,
  MDSDialog,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSText,
  MDSTextVariant,
} from "@/mds";
import { MDSZIndexes } from "@/mds/foundations";
import type { ReminderShillUpsellAttributionContext } from "@/modules/reminder-shill-upsell-attribution";
import { useAppStore } from "@/store";

interface SettingsMemAgentReminderShillSetupDialogProps {
  isOpen: boolean;
  reminderShillUpsellAttributionContext: ReminderShillUpsellAttributionContext | null;
  userEmail: string;
  onClose: () => void;
}

const focusableSelector = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * Shows the reminder shill setup prompt above the already-open Mem Agent settings modal.
 */
export function SettingsMemAgentReminderShillSetupDialog({
  isOpen,
  reminderShillUpsellAttributionContext,
  userEmail,
  onClose,
}: SettingsMemAgentReminderShillSetupDialogProps) {
  const { store } = useAppStore();
  const hasTrackedVisibleRef = useRef(false);
  const dialogContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      hasTrackedVisibleRef.current = false;
      return;
    }
    if (hasTrackedVisibleRef.current || !reminderShillUpsellAttributionContext) {
      return;
    }

    hasTrackedVisibleRef.current = true;
    trackEvent(TrackedEvent.ReminderShillSetupDialogVisible, {
      source: reminderShillUpsellAttributionContext.source,
      user_email: userEmail,
      ...buildSubscriptionPlanKindMetricsPayload({ store }),
      note_id: reminderShillUpsellAttributionContext.noteId,
      reminder_shill_id: reminderShillUpsellAttributionContext.reminderShillId,
      reminder_shill_text: reminderShillUpsellAttributionContext.generatedReminderText,
    });
  }, [isOpen, reminderShillUpsellAttributionContext, store, userEmail]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previouslyFocusedElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogContainerRef.current?.focus();

    return () => previouslyFocusedElement?.focus();
  }, [isOpen]);

  const handleOverlayClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const handleDialogKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab") {
      return;
    }

    const focusableElements = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(focusableSelector)
    ).filter(element => !element.hasAttribute("disabled") && element.tabIndex !== -1);

    if (focusableElements.length === 0) {
      event.preventDefault();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.key !== "Escape") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      onClose();
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <StyledOverlay data-mds-name="reminder-shill-setup-overlay" onClick={handleOverlayClick}>
      <StyledDialogContainer
        ref={dialogContainerRef}
        tabIndex={-1}
        onClick={event => event.stopPropagation()}
        onKeyDown={handleDialogKeyDown}
      >
        <StyledDialogShell>
          <MDSDialog isOpen={isOpen} handleClose={onClose}>
            <MDSText slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
              Set up Mem Agent to get reminders
            </MDSText>
            <StyledDialogTextContent slot={MDSDialogSlotName.Content}>
              <MDSText variant={MDSTextVariant.ParagraphS}>
                Get reminded about tasks, follow-ups, and deadlines from your notes with Mem Agent.
                Requires a Proactive account.
              </MDSText>
            </StyledDialogTextContent>
            <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
              <MDSButton variant={MDSButtonVariant.Contrast} onClick={onClose}>
                Set up Mem Agent
              </MDSButton>
            </MDSButtonGroup>
          </MDSDialog>
        </StyledDialogShell>
      </StyledDialogContainer>
    </StyledOverlay>
  );
}

const StyledOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${MDSZIndexes.StandardModal};
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(0 0 0 / 14%);
`;

const StyledDialogContainer = styled.div`
  outline: none;
`;

const StyledDialogShell = styled.div`
  .mds-dialog {
    width: min(579px, calc(100vw - 32px));
  }
`;

const StyledDialogTextContent = styled(MDSDialogTextContent)`
  max-width: 500px;
`;
